import type { VeevaVaultAssetImportStatus } from '@atlas/types'
import { EmptyState, ErrorState, ListSkeleton, PaginationFooter } from '@atlas/ui-components'
import { Button, cn } from '@atlas/ui-primitives'
import { CloudDownload } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useStartVeevaVaultImport, useVeevaVaultImport, useVeevaVaultImports } from '@/hooks/useVeevaVault'
import { notification } from '@/notifications/notification-store'
import { TextField } from '@/pages/Classification/FormField'
import type { VeevaVaultAssetImport } from '@/services/veeva-vault.service'
import { importErrorMessage, listErrorMessage } from './errors'
import { ImportRow } from './ImportRow'
import { StatusFilters } from './StatusFilters'

/**
 * ADMIN trial of the Veeva Vault import: a form to copy an asset code's latest document version into
 * ATLAS storage in the background, and every asset code imported so far, each expandable to its files.
 */
export function VeevaVaultDownloadPage() {
	const [input, setInput] = useState('')
	const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
	const [requested, setRequested] = useState<VeevaVaultAssetImport>()
	const [importError, setImportError] = useState<string>()
	const start = useStartVeevaVaultImport()
	const { imports, filter, applyFilter, page, setPage } = useImportListing()
	const requestedImport = useVeevaVaultImport(requested).data

	const toggle = (processId: string) =>
		setOpen((prev) => {
			const next = new Set(prev)
			if (!next.delete(processId)) next.add(processId)
			return next
		})

	const edit = (value: string) => {
		setInput(value)
		setImportError(undefined)
	}

	const submit = (event: FormEvent) => {
		event.preventDefault()
		const code = input.trim()
		if (!code) return
		start.mutate(code, {
			onSuccess: ({ started, assetImport }) => {
				setOpen((prev) => new Set(prev).add(assetImport.processId))
				setRequested(assetImport)
				applyFilter(undefined)
				setInput('')
				if (started) notification.success(`Import of ${code} started.`)
				else notification.info(`${code} is already imported, here are its files.`)
			},
			onError: (error) => {
				const message = importErrorMessage(error, code)
				setImportError(message)
				notification.error(message)
			},
		})
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-ds-6 p-ds-6">
			<header className="flex flex-col gap-ds-1">
				<h1 className="font-ds-display text-ds-2xl text-ds-text">Veeva Vault import</h1>
				<p className="max-w-2xl text-ds-sm text-ds-text-muted">
					Bring a Veeva Vault document into ATLAS by its asset code: the source file and every rendition download in the
					background. A code imported before opens its existing files instead of importing again.
				</p>
			</header>

			<form
				onSubmit={submit}
				className="flex flex-col gap-ds-3 rounded-ds-lg border border-ds-border bg-ds-surface p-ds-4 sm:flex-row sm:items-start"
			>
				<div className="flex-1">
					<TextField
						label="Asset code"
						value={input}
						onChange={edit}
						placeholder="ASSET-12345678"
						error={importError}
					/>
				</div>
				<Button type="submit" className="sm:mt-5.5" isDisabled={start.isPending || !input.trim()}>
					<CloudDownload aria-hidden className="mr-ds-2 h-4 w-4" />
					{start.isPending ? 'Starting…' : 'Import'}
				</Button>
			</form>

			<section aria-label="Imported assets" className="flex flex-col gap-ds-3">
				{imports.data && total(imports.data.counts) > 0 && (
					<StatusFilters counts={imports.data.counts} value={filter} onChange={applyFilter} />
				)}

				<RequestedImport
					assetImport={requestedImport}
					page={imports.data?.data}
					isOpen={!!requestedImport && open.has(requestedImport.processId)}
					onToggle={toggle}
					onDismiss={() => setRequested(undefined)}
				/>

				<ImportList
					imports={imports}
					open={open}
					fresh={requested?.processId}
					onToggle={toggle}
					onShowAll={() => applyFilter(undefined)}
				/>

				{imports.data && (
					<PaginationFooter
						page={page}
						totalPages={imports.data.pagination.totalPages}
						total={imports.data.pagination.total}
						noun="import"
						nounPlural="imports"
						isFetching={imports.isPlaceholderData}
						onPage={setPage}
					/>
				)}
			</section>
		</div>
	)
}

/** The page of imports on screen, its status filter, and the page number, reset by every filter change. */
function useImportListing() {
	const [filter, setFilter] = useState<VeevaVaultAssetImportStatus>()
	const [page, setPage] = useState(1)
	const imports = useVeevaVaultImports({ page, status: filter })
	const totalPages = imports.data?.pagination.totalPages ?? 1

	useEffect(() => {
		if (imports.error) notification.error(listErrorMessage(imports.error))
	}, [imports.error])

	// A refetch can shrink the result under a high page, e.g. the last failed import of page 3 completes.
	useEffect(() => {
		if (page > totalPages) setPage(totalPages)
	}, [page, totalPages])

	const applyFilter = (status: VeevaVaultAssetImportStatus | undefined) => {
		setFilter(status)
		setPage(1)
	}

	return { imports, filter, applyFilter, page, setPage }
}

const total = (counts: Record<string, number>) => Object.values(counts).reduce((a, b) => a + b, 0)

interface RequestedImportProps {
	assetImport: VeevaVaultAssetImport | undefined
	/** The page on screen: an import it holds is shown there instead. */
	page: VeevaVaultAssetImport[] | undefined
	isOpen: boolean
	onToggle: (processId: string) => void
	onDismiss: () => void
}

/** The import just asked for, pinned above the list while the page on screen does not hold it. */
function RequestedImport({ assetImport, page, isOpen, onToggle, onDismiss }: RequestedImportProps) {
	if (!assetImport || !page || page.some((i) => i.processId === assetImport.processId)) return null
	return (
		<div className="flex flex-col gap-ds-2 border-ds-border border-b pb-ds-3">
			<div className="flex items-center justify-between gap-ds-2">
				<p className="text-ds-xs text-ds-text-muted">Imported earlier, further down the list</p>
				<Button variant="ghost" size="sm" onPress={onDismiss}>
					Dismiss
				</Button>
			</div>
			<ul>
				<ImportRow assetImport={assetImport} isOpen={isOpen} isFresh onToggle={() => onToggle(assetImport.processId)} />
			</ul>
		</div>
	)
}

interface ImportListProps {
	imports: ReturnType<typeof useVeevaVaultImports>
	open: ReadonlySet<string>
	fresh?: string
	onToggle: (processId: string) => void
	onShowAll: () => void
}

/** The page of imports, or why there are none to show: still loading, unreadable, none yet, or all filtered out. */
function ImportList({ imports, open, fresh, onToggle, onShowAll }: ImportListProps) {
	const shown = imports.data?.data ?? []
	return imports.isPending ? (
		<ListSkeleton label="Loading imports" />
	) : imports.isError && !imports.data ? (
		<ErrorState
			title="Could not load the imports"
			description={listErrorMessage(imports.error)}
			action={
				<Button variant="secondary" size="sm" isDisabled={imports.isFetching} onPress={() => void imports.refetch()}>
					{imports.isFetching ? 'Trying again…' : 'Try again'}
				</Button>
			}
		/>
	) : !imports.data || total(imports.data.counts) === 0 ? (
		<EmptyState title="No asset imported yet" description="Paste an asset code above to bring its files into ATLAS." />
	) : shown.length === 0 ? (
		<EmptyState
			variant="filtered"
			title="No import matches this filter"
			action={
				<Button variant="secondary" size="sm" onPress={onShowAll}>
					Show all imports
				</Button>
			}
		/>
	) : (
		<ul
			aria-busy={imports.isPlaceholderData}
			className={cn('flex flex-col gap-ds-2 transition-opacity', imports.isPlaceholderData && 'opacity-60')}
		>
			{shown.map((i) => (
				<ImportRow
					key={i.processId}
					assetImport={i}
					isOpen={open.has(i.processId)}
					isFresh={fresh === i.processId}
					onToggle={() => onToggle(i.processId)}
				/>
			))}
		</ul>
	)
}
